import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    SignOut,
    EnvelopeSimple,
    Phone,
    MapPin,
    Trash,
    CheckCircle,
    Archive,
    ArrowClockwise,
} from "@phosphor-icons/react";
import { Logo } from "@/components/site/Logo";
import {
    api,
    authHeaders,
    setAdminToken,
    clearAdminToken,
    getAdminToken,
} from "@/lib/api";

const STATUSES = [
    { id: "new", label: "New", color: "text-brand-accent bg-brand-accent/10 ring-brand-accent/30" },
    { id: "contacted", label: "Contacted", color: "text-sky-300 bg-sky-400/10 ring-sky-400/30" },
    { id: "archived", label: "Archived", color: "text-zinc-300 bg-zinc-500/10 ring-zinc-400/20" },
];

export default function Admin() {
    const [authed, setAuthed] = useState(!!getAdminToken());

    return authed ? (
        <Dashboard onLogout={() => setAuthed(false)} />
    ) : (
        <Login onAuthed={() => setAuthed(true)} />
    );
}

const Login = ({ onAuthed }) => {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/admin/login", { password });
            setAdminToken(data.token);
            onAuthed();
            toast.success("Welcome back.");
        } catch (err) {
            toast.error("Invalid password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid min-h-screen place-items-center bg-brand-bg px-6">
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md rounded-3xl border border-brand-border bg-brand-surface p-8 md:p-10"
                data-testid="admin-login-card"
            >
                <Link to="/" className="inline-flex items-center">
                    <Logo size="md" showWordmark={true} />
                </Link>

                <h1 className="font-display mt-8 text-3xl font-bold tracking-tight">
                    Admin sign in
                </h1>
                <p className="mt-2 text-sm text-brand-muted">
                    Enter the dashboard password to view quote requests.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-4">
                    <div>
                        <label className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-brand-muted">
                            Password
                        </label>
                        <input
                            type="password"
                            data-testid="admin-password-input"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full rounded-xl border border-brand-border bg-brand-bg/60 px-4 py-3.5 text-sm text-brand-text focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent/60"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-button"
                        className="inline-flex w-full items-center justify-center rounded-xl bg-brand-accent px-6 py-3.5 text-sm font-bold text-[#FFFFFF] transition-colors hover:bg-brand-accentHover disabled:opacity-70"
                    >
                        {loading ? "Signing in…" : "Sign in"}
                    </button>
                </form>
            </motion.div>
        </div>
    );
};

const Dashboard = ({ onLogout }) => {
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, archived: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const handleAuthError = useCallback(() => {
        clearAdminToken();
        onLogout();
        navigate("/admin");
    }, [navigate, onLogout]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: qs }, { data: st }] = await Promise.all([
                api.get("/admin/quotes", { headers: authHeaders() }),
                api.get("/admin/stats", { headers: authHeaders() }),
            ]);
            setQuotes(qs);
            setStats(st);
        } catch (err) {
            if (err?.response?.status === 401) {
                handleAuthError();
            } else {
                toast.error("Failed to load quotes.");
            }
        } finally {
            setLoading(false);
        }
    }, [handleAuthError]);

    useEffect(() => {
        load();
    }, [load]);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/quotes/${id}`, { status }, { headers: authHeaders() });
            toast.success("Status updated.");
            load();
        } catch {
            toast.error("Update failed.");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this quote request? This cannot be undone.")) return;
        try {
            await api.delete(`/admin/quotes/${id}`, { headers: authHeaders() });
            toast.success("Deleted.");
            load();
        } catch {
            toast.error("Delete failed.");
        }
    };

    const logout = () => {
        clearAdminToken();
        onLogout();
    };

    const filtered =
        filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

    return (
        <div className="min-h-screen bg-brand-bg text-brand-text" data-testid="admin-dashboard">
            {/* Top bar */}
            <header className="sticky top-0 z-30 border-b border-brand-border/60 bg-brand-bg/85 backdrop-blur-xl">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
                    <Link to="/" className="flex items-center gap-3">
                        <Logo size="md" showWordmark={true} />
                        <span className="hidden border-l border-brand-border pl-3 text-[11px] uppercase tracking-[0.2em] text-brand-muted sm:inline">
                            Admin · Dashboard
                        </span>
                    </Link>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={load}
                            data-testid="admin-refresh"
                            className="inline-flex items-center gap-1.5 rounded-full border border-brand-border bg-brand-surface px-3.5 py-2 text-xs font-semibold text-brand-muted hover:border-brand-accent/50 hover:text-brand-text"
                        >
                            <ArrowClockwise size={14} weight="bold" />
                            Refresh
                        </button>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-1.5 rounded-full border border-brand-border bg-brand-surface px-3.5 py-2 text-xs font-semibold text-brand-muted hover:border-brand-accent/50 hover:text-brand-text"
                        >
                            <SignOut size={14} weight="bold" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-6 py-10 md:px-10">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    {[
                        { label: "Total", value: stats.total, hue: "text-brand-text" },
                        { label: "New", value: stats.new, hue: "text-brand-accent" },
                        { label: "Contacted", value: stats.contacted, hue: "text-sky-300" },
                        { label: "Archived", value: stats.archived, hue: "text-zinc-300" },
                    ].map((s) => (
                        <div
                            key={s.label}
                            data-testid={`stat-${s.label.toLowerCase()}`}
                            className="rounded-2xl border border-brand-border bg-brand-surface p-5"
                        >
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-muted">
                                {s.label}
                            </p>
                            <p className={`font-display mt-2 text-4xl font-black ${s.hue}`}>
                                {s.value}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Filter */}
                <div className="mt-10 flex flex-wrap items-center gap-2">
                    {["all", "new", "contacted", "archived"].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            data-testid={`filter-${f}`}
                            className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition-colors ${
                                filter === f
                                    ? "bg-brand-accent text-[#FFFFFF]"
                                    : "border border-brand-border bg-brand-surface text-brand-muted hover:text-brand-text"
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>

                {/* Quote list */}
                <div className="mt-6 overflow-hidden rounded-2xl border border-brand-border">
                    {loading ? (
                        <div className="grid place-items-center py-20 text-brand-muted" data-testid="admin-loading">
                            Loading…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="grid place-items-center py-20 text-brand-muted" data-testid="admin-empty">
                            No quote requests yet.
                        </div>
                    ) : (
                        <ul className="divide-y divide-brand-border">
                            {filtered.map((q) => {
                                const statusMeta =
                                    STATUSES.find((s) => s.id === q.status) ||
                                    STATUSES[0];
                                return (
                                    <li
                                        key={q.id}
                                        data-testid={`quote-row-${q.id}`}
                                        className="bg-brand-surface p-6 transition-colors hover:bg-brand-surfaceHover"
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-4">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-3">
                                                    <h3 className="font-display text-lg font-bold text-brand-text">
                                                        {q.full_name}
                                                    </h3>
                                                    <span
                                                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ring-1 ring-inset ${statusMeta.color}`}
                                                    >
                                                        {statusMeta.label}
                                                    </span>
                                                </div>
                                                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-muted">
                                                    <a
                                                        href={`tel:${q.phone}`}
                                                        className="inline-flex items-center gap-1.5 hover:text-brand-accent"
                                                    >
                                                        <Phone size={14} weight="duotone" />
                                                        {q.phone}
                                                    </a>
                                                    <a
                                                        href={`mailto:${q.email}`}
                                                        className="inline-flex items-center gap-1.5 hover:text-brand-accent"
                                                    >
                                                        <EnvelopeSimple size={14} weight="duotone" />
                                                        {q.email}
                                                    </a>
                                                    <span className="text-xs text-brand-muted/70">
                                                        {new Date(q.created_at).toLocaleString()}
                                                    </span>
                                                </div>
                                                {q.address && (
                                                    <a
                                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q.address)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="mt-2 inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-accent"
                                                    >
                                                        <MapPin size={14} weight="duotone" />
                                                        {q.address}
                                                    </a>
                                                )}
                                                {q.message && (
                                                    <p className="mt-3 max-w-3xl text-sm leading-relaxed text-brand-muted">
                                                        {q.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="flex shrink-0 items-center gap-1.5">
                                                {q.status !== "contacted" && (
                                                    <ActionBtn
                                                        onClick={() => updateStatus(q.id, "contacted")}
                                                        testid={`mark-contacted-${q.id}`}
                                                        title="Mark contacted"
                                                    >
                                                        <CheckCircle size={14} weight="bold" />
                                                    </ActionBtn>
                                                )}
                                                {q.status !== "archived" && (
                                                    <ActionBtn
                                                        onClick={() => updateStatus(q.id, "archived")}
                                                        testid={`archive-${q.id}`}
                                                        title="Archive"
                                                    >
                                                        <Archive size={14} weight="bold" />
                                                    </ActionBtn>
                                                )}
                                                <ActionBtn
                                                    onClick={() => remove(q.id)}
                                                    testid={`delete-${q.id}`}
                                                    title="Delete"
                                                    danger
                                                >
                                                    <Trash size={14} weight="bold" />
                                                </ActionBtn>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </main>
        </div>
    );
};

const ActionBtn = ({ children, onClick, testid, title, danger = false }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        title={title}
        aria-label={title}
        className={`grid h-9 w-9 place-items-center rounded-lg border transition-colors ${
            danger
                ? "border-red-500/30 bg-red-500/5 text-red-300 hover:bg-red-500/15"
                : "border-brand-border bg-brand-bg text-brand-muted hover:border-brand-accent/40 hover:text-brand-text"
        }`}
    >
        {children}
    </button>
);
